import { cn } from '@/lib/utils';
import { Button } from '@/components/base/Button';
import { ArrowRight } from 'lucide-react';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';

/**
 * Content interface for PromotionalCard component.
 * Follows content-props pattern for complete UI/content separation.
 */
export interface PromotionalCardContent {
  /** Card title text */
  title: string;
  /** Card body description text */
  bodyText: string;
  /** Card hero image */
  image: {
    src: string;
    alt: string;
  };
  /** CTA button label text */
  ctaLabel: string;
  /** CTA button destination URL */
  ctaHref: string;
  /** Optional icon name for the CTA button */
  ctaIcon?: string;
}

/**
 * Props interface for PromotionalCard component.
 */
export interface PromotionalCardProps {
  /** Content for the promotional card. If undefined, renders nothing. */
  content?: PromotionalCardContent;
  /**
   * Visual variant for the card.
   * - 'default': Large card for Umbrella landing page (432x630px, 32px title, 24px body)
   * - 'compact': Compact sidebar card (auto height, 18px title, 16px body, secondary CTA)
   * @default 'default'
   */
  variant?: 'default' | 'compact';
  /** Whether font enlargement is active (affects compact variant only) */
  isFontEnlarged?: boolean;
  /**
   * HTML heading level for the card title.
   * Use to ensure proper heading hierarchy on the page.
   * @default 'h3'
   */
  headingLevel?: 'h2' | 'h3' | 'h4';
  /** Additional CSS classes */
  className?: string;
}

/**
 * PromotionalCard - A promotional card component for the Umbrella Landing page.
 *
 * Displays an image, title, body text, and CTA button. Used as a reusable
 * card for the three main tools: Quality Care Navigator, Healthy Aging
 * Resource Hub, and Aging Data Explorer.
 *
 * @example
 * ```tsx
 * import { PromotionalCard } from '@/components';
 *
 * <PromotionalCard
 *   content={{
 *     title: "Quality Care Navigator",
 *     bodyText: "Platform to search for long-term and community care options...",
 *     image: { src: "/images/umbrella/PromotionalCard-CareNavigator-Desktop-Image.png", alt: "Care Navigator" },
 *     ctaLabel: "Go to tool",
 *     ctaHref: "https://aging.ohio.gov/care-and-living/long-term-care-quality-navigator",
 *   }}
 * />
 * ```
 */
export function PromotionalCard({ content, variant = 'default', isFontEnlarged = false, headingLevel = 'h3', className }: PromotionalCardProps) {
  const Heading = headingLevel;
  const isMobile = useIsMobile();

  if (!content) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <article
        className={cn(
          'bg-white border border-[#EBEBEB] rounded-[4px] flex flex-col',
          className
        )}
        style={{
          padding: '16px',
          minHeight: isFontEnlarged ? 'auto' : '339px',
          gap: '12px',
          boxShadow: '0px 4px 4px 0px #B6B6B640',
        }}
      >
        <img
          src={content.image.src}
          alt=""
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
        {/* Wrapper keeps title + description together so the outer 12px gap
            only applies between image, this wrapper, and the CTA button. */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Heading
            style={{
              fontFamily: "'Source Sans Pro', sans-serif",
              fontWeight: 600,
              fontSize: isFontEnlarged ? '32px' : '24px',
              lineHeight: isFontEnlarged ? '100%' : '32px',
              color: '#000000',
              margin: 0,
            }}
          >
            {content.title}
          </Heading>
          <p
            style={{
              fontFamily: "'Source Sans Pro', sans-serif",
              fontWeight: 400,
              fontSize: isFontEnlarged ? '24px' : '16px',
              lineHeight: isFontEnlarged ? '100%' : '24px',
              color: '#333333',
              margin: 0,
              marginTop: '8px',
            }}
          >
            {content.bodyText}
          </p>
        </div>
        <a
          href={content.ctaHref}
          {...(content.ctaHref.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="promotional-card-compact-cta"
          style={{
            width: isMobile ? '100%' : '145px',
            height: isMobile ? '48px' : '40px',
            borderRadius: '4px',
            border: '1px solid #0A4D8F',
            backgroundColor: 'transparent',
            padding: '8px 16px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: "'Source Sans Pro', sans-serif",
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#0A4D8F',
            textDecoration: 'none',
            marginTop: isFontEnlarged ? '12px' : '0',
          }}
        >
          {content.ctaLabel}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </a>
        <style>{`
          .promotional-card-compact-cta:hover {
            background-color: #F0F7FF !important;
          }
        `}</style>
      </article>
    );
  }

  return (
    <article
      className={cn(
        'flex bg-white border border-[#EBEBEB] rounded-[4px] shadow-[0px_4px_4px_0px_rgba(187,187,187,0.25)]',
        'flex-col min-[800px]:flex-row min-[1200px]:flex-col',
        'w-full min-[1200px]:max-w-[432px] min-[1200px]:h-[630px]',
        'gap-5 pb-6 min-[800px]:pb-0 min-[1200px]:pb-6',
        'overflow-hidden',
        className
      )}
    >
      {/* Card Image — padded at all viewports; at tablet, constrained to text content height */}
      <div className="px-5 pt-5 min-[800px]:pb-5 min-[1200px]:pb-0 min-[800px]:w-1/2 min-[1200px]:w-full min-[800px]:min-h-0 overflow-hidden rounded-[4px]">
        <img
          src={content.image.src}
          alt=""
          className="w-full object-cover scale-[1.01] min-[800px]:h-full min-[1200px]:h-auto"
        />
      </div>

      {/* Card Content + CTA wrapper for tablet horizontal layout */}
      <div className="flex flex-col flex-1 gap-5 min-[800px]:gap-4 min-[1200px]:gap-5 min-[800px]:py-5 min-[1200px]:py-0">
        {/* Card Content Area — 12px gap between title/body at tablet */}
        <div className="flex flex-col gap-5 min-[800px]:gap-3 min-[1200px]:gap-5 px-5 flex-1">
          {/* Card Title */}
          <Heading
            className="font-semibold text-[32px] leading-[40px] text-black font-['Source_Sans_Pro'] min-[800px]:text-[24px] min-[800px]:leading-[32px] min-[1200px]:text-[32px] min-[1200px]:leading-[40px]"
          >
            {content.title}
          </Heading>

          {/* Body Text */}
          <p
            className="font-normal text-[24px] leading-[32px] text-black font-['Source_Sans_Pro'] min-[800px]:text-[16px] min-[800px]:leading-[24px] min-[1200px]:text-[24px] min-[1200px]:leading-[32px]"
          >
            {content.bodyText}
          </p>
        </div>

        {/* CTA Button */}
        <div className="px-5 mt-auto min-[800px]:pb-5 min-[1200px]:pb-0">
          <Button
            variant="primary"
            size="large"
            asChild
            className="w-full rounded-[6px] min-[800px]:w-auto min-[1200px]:w-full"
          >
            <a
              href={content.ctaHref}
              {...(content.ctaHref.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {content.ctaLabel}
              <ArrowRight className="h-6 w-6 ml-2" aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>
    </article>
  );
}

PromotionalCard.displayName = 'PromotionalCard';
